import { useEffect } from "react";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { useStore } from "@/context/StoreContext";

export default function Contact() {
  const { config } = useStore();

  useEffect(() => {
    document.title = "Contact — THE GOOD GRID";
  }, []);

  const wa1 = (config.whatsapp_number || "").replace(/\D/g, "");
  const wa2 = (config.whatsapp_number_2 || "").replace(/\D/g, "");

  const rows = [
    {
      icon: Mail, label: "Email", testid: "contact-email",
      value: config.contact_email || "Shared on enquiry",
      href: config.contact_email ? `mailto:${config.contact_email}` : null,
    },
    {
      icon: Phone, label: "Phone", testid: "contact-phone",
      value: config.contact_phone || "Shared on enquiry",
      href: wa1 ? `tel:+${wa1}` : null,
    },
    {
      icon: MessageCircle, label: "WhatsApp", testid: "contact-whatsapp",
      value: wa1 ? `+91 ${wa1.slice(2)}${wa2 ? ` / +91 ${wa2.slice(2)}` : ""}` : "Shared on enquiry",
      href: wa1 ? `https://wa.me/${wa1}?text=${encodeURIComponent("Hello THE GOOD GRID, I'd like to discuss a corporate gifting requirement.")}` : null,
    },
    { icon: MapPin, label: "Location", value: "India — details on enquiry", testid: "contact-location", href: null },
  ];

  return (
    <div data-testid="contact-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl mt-4 tracking-tight leading-[1.05]">
            Let's build something<br /><span className="italic text-champagne-light">worth gifting.</span>
          </h1>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 mt-16">
          <div className="lg:col-span-4">
            <Reveal delay={0.15}>
              <p className="text-ivory/50 text-sm leading-relaxed max-w-sm mb-10">
                Tell us about your requirement and our gifting team will respond with a curated proposal and corporate pricing.
              </p>
              <div className="space-y-6">
                {rows.map((r) => (
                  <div key={r.label} className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-white/15 flex items-center justify-center shrink-0">
                      <r.icon size={15} className="text-champagne" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-ivory/40">{r.label}</p>
                      {r.href ? (
                        <a data-testid={r.testid} href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm text-ivory mt-0.5 inline-block hover:text-champagne transition-colors border-b border-transparent hover:border-champagne/50">
                          {r.value}
                        </a>
                      ) : (
                        <p data-testid={r.testid} className="text-sm text-ivory mt-0.5">{r.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.2} className="border border-white/10 bg-[#161616] p-6 sm:p-10">
              <EnquiryForm />
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
